import { useState } from "react";
import { toast } from "sonner";
import { Loader2, UploadCloud } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { uploadCreatorFile, optimizeImage } from "@/lib/upload";
import { MediaImage } from "@/components/Media";

export function uploadErrorText(e) {
  const d = e?.response?.data?.detail;
  if (d) return formatApiErrorDetail(d);
  if (e?.response?.status === 413) return "That image is too large. Please choose one under 25 MB.";
  return "The upload didn't finish — please check your connection and try again.";
}

// Profile/promo image picker: optimizes, uploads, then saves the reference. Success is only shown after the save succeeds.
export function ProfileImagePick({ label, hint, url, onSaved, save, testid, className = "h-32" }) {
  const [pct, setPct] = useState(null);
  const pick = async (file) => {
    if (!file || pct !== null) return;
    setPct(0);
    let data;
    try {
      const small = await optimizeImage(file);
      data = await uploadCreatorFile(small, setPct);
    } catch (e) {
      toast.error(`${label}: ${uploadErrorText(e)}`);
      setPct(null);
      return;
    }
    try {
      await save(data.id);
      toast.success(`${label} updated`);
      window.dispatchEvent(new Event("peeping-profile-updated"));
      onSaved?.();
    } catch (e) {
      toast.error(`Couldn't save your ${label.toLowerCase()}: ${e?.response?.data?.detail ? formatApiErrorDetail(e.response.data.detail) : "please try again."}`);
    } finally {
      setPct(null);
    }
  };
  const busy = pct !== null;
  return (
    <label className={`flex flex-col gap-2 text-xs text-[#a7a0af] ${busy ? "cursor-wait" : "cursor-pointer"}`}>
      <span>{label}{hint && <span className="ml-1 text-[#6f6878]">· {hint}</span>}</span>
      <div className={`relative flex items-center justify-center overflow-hidden rounded-xl border border-dashed border-white/20 bg-white/[0.03] ${className}`}>
        {url ? <MediaImage url={url} className="h-full w-full object-cover" /> : !busy && <UploadCloud size={22} />}
        {busy && (
          <div data-testid={`${testid}-progress`} className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/70 text-white">
            <Loader2 className="animate-spin text-[#a03cff]" size={20} />
            <span className="text-[0.65rem]">Uploading… {pct}%</span>
          </div>
        )}
      </div>
      <input type="file" accept="image/*" className="hidden" disabled={busy} data-testid={testid}
        onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; pick(f); }} />
    </label>
  );
}
